/**
 * Ported from the lekterion codebase (`src/lib/schemas/model.ts`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */

// Namespace import, not `import { z }` as the source had it — zod ships CJS
// for the node condition and the named form resolves to `undefined` under
// vitest's multi-project run (same as packages/shared/src/reader/cdn.ts).
import * as z from "zod";

/**
 * Tier-1 document model schemas (DATA-ARCHITECTURE.md §1.2–§1.4).
 *
 * The model is derived once at ingest and shipped as static JSON. Every
 * interactive layer keys off character offsets into one immutable `text`
 * string, so the schemas below are mostly `{ start, end }` spans.
 *
 * Zod is the single source of truth; TS types are derived via `z.infer`,
 * and the same schema validates the JSON when the client loads it.
 */

export const Lang = z.enum(["de", "grc", "la"]);
export type Lang = z.infer<typeof Lang>;

export const Author = z.enum([
  "Husserl",
  "Heidegger",
  "Kant",
  "Aristotle",
  "Descartes",
]);
export type Author = z.infer<typeof Author>;

export const LocusSchemeId = z.enum([
  "sz-niemeyer",
  "ideen",
  "kant-AB",
  "kant-akademie",
  "bekker",
  "at",
]);
export type LocusSchemeId = z.infer<typeof LocusSchemeId>;

/** A locus anchor: `<a id="page012">`, `<a id="980a21">`, `<a id="AT7p003">` … */
export const Anchor = z.object({
  /** Raw anchor id from the `.md`. */
  id: z.string(),
  /** Char index into `text` where it sits (invisible — §1). */
  offset: z.number().int().nonnegative(),
  /** Display locus drawn from `id`, e.g. "12", "IV 389", "A1", "980a21". */
  locus: z.string(),
});
export type Anchor = z.infer<typeof Anchor>;

export const Block = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("para"),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("heading"),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
    level: z.number().int().nonnegative(),
    headingId: z.string(),
  }),
  z.object({
    kind: z.literal("footnoteDef"),
    ref: z.string(),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
  }),
]);
export type Block = z.infer<typeof Block>;

/** Breadcrumb / TOC node. `level` is normalized depth, NOT the raw `#`/`##`. */
export interface HeadingNode {
  headingId: string;
  level: number;
  label: string;
  /** "§ 1" where the text uses § sections (German); absent otherwise. */
  sectionMark?: string;
  start: number;
  end: number;
  children: HeadingNode[];
}
export const HeadingNode: z.ZodType<HeadingNode> = z.lazy(() =>
  z.object({
    headingId: z.string(),
    level: z.number().int().nonnegative(),
    label: z.string(),
    sectionMark: z.string().optional(),
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
    children: z.array(HeadingNode),
  }),
);

export const Footnote = z.object({
  ref: z.string(),
  /** Position of the `[^ref]` marker in `text`. */
  markerOffset: z.number().int().nonnegative(),
  body: z.string(),
});
export type Footnote = z.infer<typeof Footnote>;

/** A word token over `text`; `text.slice(start, end) === surface`. */
export const Token = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
  surface: z.string(),
});
export type Token = z.infer<typeof Token>;

/** An italic / emphasis run over `text` (refinement for §1 rendering). */
export const Emphasis = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});
export type Emphasis = z.infer<typeof Emphasis>;

export const DocumentModel = z.object({
  id: z.string(),
  lang: Lang,
  /** Denormalized from the catalog so the model is self-sufficient for citation. */
  locusScheme: LocusSchemeId,
  /** The clean prose string — single source of all offsets. */
  text: z.string(),
  blocks: z.array(Block),
  /** Locus anchors, in document order (monotonic — §8). */
  anchors: z.array(Anchor),
  headings: z.array(HeadingNode),
  footnotes: z.array(Footnote),
  tokens: z.array(Token),
  emphasis: z.array(Emphasis),
  /** Bumped on ingest changes; content-addresses the model (§1.6). */
  ingestVersion: z.number().int().nonnegative(),
});
export type DocumentModel = z.infer<typeof DocumentModel>;

/**
 * Facsimile delivery (§4 / DATA-ARCHITECTURE Tier 3), reified for static JSON —
 * the spec's `keyFor` *function* can't ship in JSON, so the mapping is data.
 * Asset keys are **relative**; the client prepends `VITE_FACSIMILE_BASE`
 * (default `/facsimile/` in dev, the R2 public base in prod), so one catalog
 * works for both.
 */
export const FacsimileConfig = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("images"),
    /** anchor id (`page012`) → relative asset key (`husserl/page012.png`), 1:1. */
    pages: z.record(z.string(), z.string()),
  }),
  z.object({
    kind: z.literal("pdf"),
    /** relative key of the source PDF, rendered client-side via pdf.js. */
    pdfKey: z.string(),
    /** print page = PDF page − delta ⇒ PDF page = printPage + delta (Heidegger: +13). */
    pageDelta: z.number().int(),
  }),
]);
export type FacsimileConfig = z.infer<typeof FacsimileConfig>;

/** One entry in the corpus catalog (§1.1). */
export const TextManifest = z.object({
  id: z.string(),
  author: Author,
  work: z.string(),
  lang: Lang,
  title: z.string(),
  /** Clean-text char length — drives the browse bar's proportional book tier (§2). */
  size: z.number().int().nonnegative(),
  locusScheme: LocusSchemeId,
  /** true → Husserl & Heidegger only (§4). */
  hasFacsimile: z.boolean(),
  /** Present iff `hasFacsimile` — the page→asset mapping or PDF config (§4). */
  facsimile: FacsimileConfig.optional(),
  modelUrl: z.string(),
  /** Absent for texts whose edition may not be redistributed as Markdown. */
  mdUrl: z.string().optional(),
  /** Carried from source provenance comments (e.g. Aristotle CTS URN). */
  provenance: z.string().optional(),
});
export type TextManifest = z.infer<typeof TextManifest>;

export const CorpusCatalog = z.object({
  texts: z.array(TextManifest),
  ingestVersion: z.number().int().nonnegative(),
});
export type CorpusCatalog = z.infer<typeof CorpusCatalog>;
